import React, { useState, useEffect } from 'react';
import { useAuth } from '../store/auth';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_SERVER_URL || '';

export default function NotificationSettings() {
  const { authHeaders, claims } = useAuth();
  const isSuper = !!claims?.super;

  // Super admin fields
  const [botToken, setBotToken]   = useState('');
  const [botName,   setBotName]   = useState('');
  const [botHandle, setBotHandle] = useState('');
  // Shared: both super and regular admin have a chatId
  const [chatId,   setChatId]   = useState('');
  const [hasSharedBot, setHasSharedBot] = useState(false);

  const [saving,  setSaving]  = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetch(`${API}/notifications/telegram`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setChatId(d.chat_id || '');
        setBotName(d.bot_name || '');
        setBotHandle(d.bot_handle || '');
        if (isSuper) {
          setBotToken(d.bot_token || '');
          if (d.bot_token) {
            fetch(`${API}/notifications/telegram/bot-info`, { headers: authHeaders() })
              .then(r => r.ok ? r.json() : null)
              .then(b => { if (b?.ok) { setBotName(b.bot_name); setBotHandle(b.bot_handle); } })
              .catch(() => {});
          }
        } else {
          setHasSharedBot(!!d.has_shared_bot);
        }
      })
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = isSuper
        ? { bot_token: botToken, chat_id: chatId, bot_name: botName, bot_handle: botHandle }
        : { chat_id: chatId };
      const res = await fetch(`${API}/notifications/telegram`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { toast.error('Save failed'); return; }
      toast.success('Telegram config saved');
      if (isSuper && botToken) {
        fetch(`${API}/notifications/telegram/bot-info`, { headers: authHeaders() })
          .then(r => r.ok ? r.json() : null)
          .then(b => { if (b?.ok) { setBotName(b.bot_name); setBotHandle(b.bot_handle); } })
          .catch(() => {});
      }
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!chatId) { toast.error('Enter your chat ID first'); return; }
    if (isSuper && !botToken) { toast.error('Enter the bot token first'); return; }
    setTesting(true);
    try {
      const body = isSuper
        ? { bot_token: botToken, chat_id: chatId, bot_name: botName, bot_handle: botHandle }
        : { chat_id: chatId };
      const res = await fetch(`${API}/notifications/telegram/test`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Test failed'); return; }
      toast.success('Test message sent! Check your Telegram.');
    } catch { toast.error('Test failed'); }
    finally { setTesting(false); }
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <h2 style={{ marginTop: 0, marginBottom: 4, fontWeight: 700 }}>Notification Settings</h2>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 28 }}>
        {isSuper
          ? 'Set up the shared Telegram bot. You will be notified for every new device across all admins.'
          : 'Enter your Telegram chat ID to receive notifications when your agent activates on a new PC.'}
      </p>

      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5 }}>
            Telegram {isSuper ? 'Bot (Shared)' : 'Chat ID'}
          </div>
          {isSuper && botName && botHandle && (
            <span style={{ fontSize: 12, padding: '2px 10px', background: '#0d2d1a', border: '1px solid #22c55e44', borderRadius: 12, color: '#4ade80' }}>
              🤖 {botName} &nbsp;·&nbsp; {botHandle}
            </span>
          )}
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>

          {isSuper ? (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.6 }}>
              1. Message <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>@BotFather</a> on Telegram to create a bot and get the token.<br />
              2. Fill in the bot name &amp; @ handle below — admins will see them as copyable fields.<br />
              3. Message your bot, then run <code style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3 }}>https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> to find your chat ID.
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.6 }}>
              {hasSharedBot
                ? (
                  <>
                    The shared bot has been configured. Find it on Telegram, press <b>Start</b>, then enter your chat ID below.
                    {(botName || botHandle) && (
                      <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {botName   && <CopyField label="Bot name"     value={botName} />}
                        {botHandle && <CopyField label="Bot @ handle" value={botHandle} accent />}
                      </div>
                    )}
                    <div style={{ marginTop: 10 }}>
                      To find your chat ID: message the bot above and visit:<br />
                      <code style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3 }}>https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code>
                    </div>
                  </>
                )
                : <span style={{ color: '#f59e0b' }}>⚠ The super admin has not configured the shared bot yet. Ask them to set it up first.</span>
              }
            </div>
          )}

          {isSuper && (
            <>
              <label style={labelStyle}>Bot Token</label>
              <input
                value={botToken}
                onChange={e => { setBotToken(e.target.value); setBotName(''); setBotHandle(''); }}
                placeholder="123456789:ABCdef..."
                style={inputStyle}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Bot display name</label>
                  <input
                    value={botName}
                    onChange={e => setBotName(e.target.value)}
                    placeholder="e.g. ClassRMM Alerts"
                    style={{ ...inputStyle, fontFamily: 'inherit', marginBottom: 0 }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Bot @ handle</label>
                  <input
                    value={botHandle}
                    onChange={e => setBotHandle(e.target.value)}
                    placeholder="@classrmm_bot"
                    style={{ ...inputStyle, marginBottom: 0 }}
                  />
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, marginBottom: 12 }}>
                Name and handle are shown to admins so they can find the bot on Telegram.
              </div>
            </>
          )}

          <label style={labelStyle}>Your Chat ID</label>
          <input
            value={chatId}
            onChange={e => setChatId(e.target.value)}
            placeholder="Your Telegram chat ID (e.g. 123456789)"
            style={{ ...inputStyle, marginBottom: 20 }}
          />

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSave} disabled={saving} style={btn('var(--accent)')}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={handleTest} disabled={testing || (!isSuper && !hasSharedBot)} style={btn()}>
              {testing ? 'Sending...' : 'Send Test Message'}
            </button>
            {(chatId || (isSuper && botToken)) && (
              <button onClick={() => { setChatId(''); if (isSuper) { setBotToken(''); setBotName(''); setBotHandle(''); } }} style={btn('#ef444488')}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>
        {isSuper
          ? 'All admins use this shared bot. Each admin enters their own chat ID to receive their notifications.'
          : 'Notifications are sent only for devices that connect with your agent token.'}
      </div>
    </div>
  );
}

function CopyField({ label, value, accent }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 0,
      background: 'var(--bg)', border: '1px solid var(--border)',
      borderRadius: 6, overflow: 'hidden', maxWidth: 260,
    }}>
      <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--muted)',
                    borderRight: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
        {label}
      </div>
      <div style={{
        padding: '6px 10px', fontSize: 13, fontFamily: 'monospace',
        color: accent ? 'var(--accent)' : 'var(--text)',
        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {value}
      </div>
      <button
        onClick={copy}
        title="Copy"
        style={{
          padding: '6px 10px', background: 'transparent',
          border: 'none', borderLeft: '1px solid var(--border)',
          cursor: 'pointer', color: copied ? 'var(--green)' : 'var(--muted)',
          fontSize: 13, flexShrink: 0,
        }}>
        {copied ? '✓' : '⎘'}
      </button>
    </div>
  );
}

const btn = (color) => ({
  padding: '8px 16px', fontSize: 13, fontWeight: color ? 600 : 400,
  background: color ? `${color}22` : 'transparent',
  border: `1px solid ${color || 'var(--border)'}`,
  borderRadius: 6, color: color || 'var(--muted)',
  cursor: 'pointer', whiteSpace: 'nowrap',
});

const inputStyle = {
  display: 'block', width: '100%', marginBottom: 12, padding: '9px 12px',
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
  color: 'var(--text)', fontSize: 13, boxSizing: 'border-box', fontFamily: 'monospace',
};

const labelStyle = { display: 'block', marginBottom: 4, color: 'var(--muted)', fontSize: 12 };
